import {
  Box,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Picture, TImage } from '@shared/ui'

import { ReactComponent as AiIcon } from '../../assets/ai.svg'
import arbipointPng from '../../assets/arbipoint.png'
import arbipointWebp from '../../assets/arbipoint.webp'
import boardPng from '../../assets/board-bg.png'
import { ReactComponent as BuildingIcon } from '../../assets/building.svg'
import { ReactComponent as FlagIcon } from '../../assets/flag.svg'
import { ReactComponent as KioskIcon } from '../../assets/kiosk.svg'
import { ReactComponent as MobileIcon } from '../../assets/mobile.svg'
import { ReactComponent as PrintCashIcon } from '../../assets/print-cash.svg'
import { ReactComponent as QrCodeIcon } from '../../assets/qr-code.svg'
import { ReactComponent as UmbrellaIcon } from '../../assets/umberella.svg'

import { ProjectTitle, ProjectName } from '../components'

const BoardElement = styled('div')({})

const Board = styled('div')(({ theme }) => ({
  position: 'relative',
  background:
    'linear-gradient(134.87deg, #E7ECF4 8.95%, #D0D8ED 53.4%, #CDDBFB 90.26%)',
  width: '100%',
  height: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'repeat(6, auto)',
  gridAutoFlow: 'row',
  rowGap: theme.spacing(2.5),
  columnGap: theme.spacing(7.5),
  padding: theme.spacing(4, 3),
  minHeight: '420px',
  [theme.breakpoints.up('md')]: {
    rowGap: theme.spacing(4),
    backgroundImage: `url(${boardPng})`,
    backgroundSize: '100% 100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'repeat(3, auto)',
    gridAutoFlow: 'column',
    padding: theme.spacing(9, 4),
  },
}))

const iconStyles = {
  xs: {
    width: '20px',
    height: '20px',
  },
  sm: {
    width: '32px',
    height: '32px',
  },
  md: {
    width: '40px',
    height: '40px',
  },
}

const boadIconStyles = {
  xs: {
    minWidth: '64px',
    height: '64px',
  },
  md: {
    minWidth: '72px',
    height: '72px',
  },
}

export function ArbipointProject() {
  const { t } = useTranslation('widgets')

  const theme = useTheme()

  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'))
  const isSmAndUp = useMediaQuery(theme.breakpoints.up('sm'))

  const iconsStyle = useMemo(() => {
    if (isMdAndUp) {
      return 'md'
    }

    if (isSmAndUp) {
      return 'sm'
    }

    return 'xs'
  }, [isMdAndUp, isSmAndUp])

  return (
    <Stack>
      <ProjectTitle number={1} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="flex-end"
        gap={{
          xs: 4,
          md: 7.5,
        }}
      >
        <Stack gap={{ xs: 2.5, md: 5, flex: 1 }}>
          <ProjectName
            sx={{
              mt: { xs: 3, md: 4.5 },
            }}
            name={t('main-page.projects.arbipoint.name')}
            description={t('main-page.projects.arbipoint.description')}
          />

          <Stack gap={{ xs: 1.5, md: 2.5 }}>
            <Typography
              fontStyle="italic"
              variant={isMdAndUp ? 'text20Italic' : 'text16Regular'}
            >
              {t('main-page.projects.arbipoint.text-1')}
            </Typography>

            <Typography
              sx={{ fontWeight: 400 }}
              variant={isMdAndUp ? 'text18Regular' : 'text16Regular'}
            >
              {t('main-page.projects.arbipoint.text-2')}
            </Typography>
          </Stack>
        </Stack>

        <Picture
          png={arbipointPng as TImage<'png'>}
          webp={arbipointWebp as TImage<'webp'>}
          alt="Arbipoint"
          sx={{
            flex: 1,
            maxHeight: { md: '370px' },
          }}
        />
      </Stack>

      <Board
        sx={{
          margin: { xs: '32px -24px 0', md: '100px 0 0' },
          width: { xs: 'calc(100% + 48px)', md: '100%' },
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        />

        <BoardElement
          sx={
            !isMdAndUp
              ? {
                  padding: '12px 14px',
                  background: '#fff',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }
              : {
                  position: 'absolute',
                  top: 14,
                  left: 24,
                  right: 0,
                  bottom: 0,
                }
          }
        >
          <Stack
            direction="row"
            width={{ xs: '100%', md: 'auto' }}
            gap={{ xs: 1, md: 7.5 }}
            justifyContent={{ xs: 'space-between', md: 'flex-start' }}
            paddingX={{ sm: 6, md: 0 }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <MobileIcon style={iconStyles[iconsStyle]} />

              <Typography variant="text14Regular" textTransform="uppercase">
                {t('main-page.projects.arbipoint.board.top-icons.online')}
              </Typography>
            </Stack>

            <Stack direction="row" gap={1} alignItems="center">
              <KioskIcon style={iconStyles[iconsStyle]} />

              <Typography variant="text14Regular" textTransform="uppercase">
                {t('main-page.projects.arbipoint.board.top-icons.kiosk')}
              </Typography>
            </Stack>

            <Stack direction="row" gap={1} alignItems="center">
              <AiIcon style={iconStyles[iconsStyle]} />

              <Typography variant="text14Regular" textTransform="uppercase">
                {t('main-page.projects.arbipoint.board.top-icons.ai-support')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" gap={2.5}>
            <FlagIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipoint.board.elements.0.title')}
              </Typography>

              <Typography variant="text16Regular">
                {t('main-page.projects.arbipoint.board.elements.0.description')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" gap={2.5}>
            <PrintCashIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipoint.board.elements.1.title')}
              </Typography>

              <Typography variant="text16Regular">
                {t('main-page.projects.arbipoint.board.elements.1.description')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" gap={2.5}>
            <UmbrellaIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipoint.board.elements.2.title')}
              </Typography>

              <Typography variant="text16Regular">
                {t('main-page.projects.arbipoint.board.elements.2.description')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" gap={2.5}>
            <BuildingIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipoint.board.elements.3.title')}
              </Typography>

              <Typography variant="text16Regular">
                {t('main-page.projects.arbipoint.board.elements.3.description')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>

        <BoardElement>
          <Stack direction="row" gap={2.5}>
            <QrCodeIcon style={boadIconStyles[isMdAndUp ? 'md' : 'xs']} />

            <Stack>
              <Typography variant="text18Bold">
                {t('main-page.projects.arbipoint.board.elements.4.title')}
              </Typography>

              <Typography variant="text16Regular">
                {t('main-page.projects.arbipoint.board.elements.4.description')}
              </Typography>
            </Stack>
          </Stack>
        </BoardElement>
      </Board>
    </Stack>
  )
}
